import { Link } from 'react-router-dom';
import type { BarDuration, ChartMarker, OBVAnalyzerEvent } from '@einhaus/project314-types';
import ChartContainer from '@/components/Chart/ChartContainer';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import Card from '@/components/UI/Card';
import {
    buildAssetChartUrl,
    buildCompactMarkerText,
    formatEnumLabel,
    formatMetric,
    getEventTone,
    getToneBadgeVariant,
    getToneMarkerLabel,
    getToneSummaryLabel
} from './helpers';
import MetricPill from './MetricPill';
import type { ObvAuditEntity, ObvAuditEventRow, ObvChartWindow, ObvEventCountRow } from './types';

interface PriceChartCardProps {
    chartWindow: ObvChartWindow;
    entity: ObvAuditEntity;
    eventCounts: ObvEventCountRow[];
    filteredEventDateTimeCount: number;
    markers: ChartMarker[];
    onFocusRelativeEvent: (offset: -1 | 1) => void;
    onSelectAllEventTypes: () => void;
    onToggleEventType: (eventType: OBVAnalyzerEvent['type']) => void;
    onToggleFocusedMarkersOnly: () => void;
    resolvedDuration: BarDuration;
    resolvedSelectedEventTypes: Set<OBVAnalyzerEvent['type']>;
    resolvedShowFocusedMarkersOnly: boolean;
    selectedDateTime: string | null;
    selectedEventDateTimeIndex: number;
    selectedEventMarkerLabelByKey: Map<string, string>;
    selectedEvents: ObvAuditEventRow[];
}

const PriceChartCard = ({
    chartWindow,
    entity,
    eventCounts,
    filteredEventDateTimeCount,
    markers,
    onFocusRelativeEvent,
    onSelectAllEventTypes,
    onToggleEventType,
    onToggleFocusedMarkersOnly,
    resolvedDuration,
    resolvedSelectedEventTypes,
    resolvedShowFocusedMarkersOnly,
    selectedDateTime,
    selectedEventDateTimeIndex,
    selectedEventMarkerLabelByKey,
    selectedEvents
}: PriceChartCardProps) => (
    <Card responsivePadding bodyClassName="space-y-4" title="Price Chart With Filtered OBV Event Markers">
        <div className="flex flex-wrap gap-3">
            <Button
                size="sm"
                to={buildAssetChartUrl({
                    assetId: entity.id,
                    duration: resolvedDuration,
                    startDateTime: chartWindow.startDateTime,
                    endDateTime: chartWindow.endDateTime
                })}
                variant="secondary"
            >
                Open Asset Chart
            </Button>
            <Link className="text-sm text-blue-400 hover:text-blue-300 underline" to={`/asset/${entity.id}`}>
                Open Asset Page
            </Link>
        </div>

        <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-950/70 p-4">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        resolvedShowFocusedMarkersOnly
                            ? 'border-blue-500/50 bg-blue-500/15 text-blue-200'
                            : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500'
                    }`}
                    type="button"
                    onClick={onToggleFocusedMarkersOnly}
                >
                    {resolvedShowFocusedMarkersOnly ? 'Focused markers only' : 'All filtered markers in window'}
                </button>
                <button
                    className="rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-300 transition-colors hover:border-zinc-500"
                    type="button"
                    onClick={onSelectAllEventTypes}
                >
                    Reset event filters
                </button>
                <div className="text-xs text-zinc-500">
                    Showing {markers.length} marker{markers.length === 1 ? '' : 's'} from {resolvedSelectedEventTypes.size} event type
                    {resolvedSelectedEventTypes.size === 1 ? '' : 's'}
                    {resolvedShowFocusedMarkersOnly && selectedDateTime ? ` around ${selectedDateTime}` : ' in the visible chart window'}.
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                {eventCounts.map((row) => {
                    const isActive = resolvedSelectedEventTypes.has(row.type);

                    return (
                        <button
                            aria-pressed={isActive}
                            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                                isActive
                                    ? 'border-cyan-500/50 bg-cyan-500/15 text-cyan-100'
                                    : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200'
                            }`}
                            key={row.type}
                            type="button"
                            onClick={() => onToggleEventType(row.type)}
                        >
                            {formatEnumLabel(row.type)} · {row.count}
                        </button>
                    );
                })}
            </div>
        </div>

        <div className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-950/70 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Focused Event Review</div>
                    <div className="mt-1 text-sm text-zinc-300">
                        Use this panel as the source of truth for the selected event bar. The chart markers are only visual anchors.
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        disabled={selectedEventDateTimeIndex <= 0}
                        size="sm"
                        type="button"
                        variant="outline-dark"
                        onClick={() => onFocusRelativeEvent(-1)}
                    >
                        Newer Event
                    </Button>
                    <Button
                        disabled={selectedEventDateTimeIndex === -1 || selectedEventDateTimeIndex >= filteredEventDateTimeCount - 1}
                        size="sm"
                        type="button"
                        variant="outline-dark"
                        onClick={() => onFocusRelativeEvent(1)}
                    >
                        Older Event
                    </Button>
                </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <MetricPill label="Focused DateTime" value={selectedDateTime ?? 'n/a'} />
                <MetricPill label="Events On Focused Bar" value={String(selectedEvents.length)} />
                <MetricPill
                    label="Event Position"
                    value={selectedEventDateTimeIndex === -1 ? 'n/a' : `${selectedEventDateTimeIndex + 1} / ${filteredEventDateTimeCount}`}
                />
                <MetricPill label="Marker Mode" value={resolvedShowFocusedMarkersOnly ? 'Focused neighborhood' : 'Full visible window'} />
            </div>

            {selectedEvents.length > 0 ? (
                <div className="rounded-lg border border-zinc-800 bg-black/20 p-4">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Chart Marker Guide</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {selectedEvents.map((eventRow) => {
                            const eventKey = `${eventRow.dateTime}:${eventRow.event.type}:${eventRow.event.barIndex}`;

                            const markerLabel = selectedEventMarkerLabelByKey.get(eventKey) ?? buildCompactMarkerText(eventRow.event);

                            return (
                                <Badge className="px-3 py-1" key={eventKey} variant={getToneBadgeVariant(getEventTone(eventRow.event))}>
                                    {markerLabel} = {formatEnumLabel(eventRow.event.type)}
                                </Badge>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {selectedEvents.length === 0 ? (
                <div className="rounded-lg border border-zinc-800 bg-black/20 p-4 text-sm text-zinc-400">
                    No filtered OBV event exists on the currently focused datetime.
                </div>
            ) : (
                <div className="grid gap-3 lg:grid-cols-2">
                    {selectedEvents.map((eventRow) => {
                        const eventTone = getEventTone(eventRow.event);

                        return (
                            <div
                                className="rounded-lg border border-zinc-800 bg-black/20 p-4"
                                key={`${eventRow.dateTime}:${eventRow.event.type}:${eventRow.event.barIndex}`}
                            >
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge variant={getToneBadgeVariant(eventTone)}>
                                        {selectedEventMarkerLabelByKey.get(
                                            `${eventRow.dateTime}:${eventRow.event.type}:${eventRow.event.barIndex}`
                                        ) ?? getToneMarkerLabel(eventTone)}
                                    </Badge>
                                    <Badge variant={getToneBadgeVariant(eventTone)}>{getToneSummaryLabel(eventTone)}</Badge>
                                    <Badge variant="dark">{formatEnumLabel(eventRow.event.type)}</Badge>
                                    {eventRow.event.divergenceType ? (
                                        <Badge variant="secondary">{formatEnumLabel(eventRow.event.divergenceType)}</Badge>
                                    ) : null}
                                </div>

                                <div className="mt-3 text-lg font-semibold text-zinc-100">{buildCompactMarkerText(eventRow.event)}</div>
                                <div className="mt-1 text-sm text-zinc-400">{eventRow.dateTime}</div>

                                <div className="mt-4 grid gap-3 md:grid-cols-2">
                                    <MetricPill label="Flow Balance 20" value={formatMetric(eventRow.event.flowBalance20, 3)} />
                                    <MetricPill label="Signal Distance" value={formatMetric(eventRow.event.signalDistance, 3)} />
                                    <MetricPill label="Local Percentile" value={formatMetric(eventRow.event.localPercentile, 3)} />
                                    <MetricPill
                                        label="Bars Since Divergence"
                                        value={
                                            eventRow.event.barsSinceDivergence === null ? 'n/a' : String(eventRow.event.barsSinceDivergence)
                                        }
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>

        <ChartContainer
            hideComparisonSearch
            shouldIncludeVolumeSeries
            duration={resolvedDuration}
            endDateTime={chartWindow.endDateTime}
            entity={entity}
            markersOverride={markers}
            startDateTime={chartWindow.startDateTime}
        />
    </Card>
);

export default PriceChartCard;
